/**
 * Plugin font API.
 *
 * The platform vendors a catalog of fonts to its CDN and the host page
 * injects their `@font-face` declarations into every plugin iframe up
 * front (the `.woff2` bytes stay lazy — a face only downloads when a
 * glyph in it is actually painted). A plugin therefore just:
 *
 *   1. lists the catalog (`FONT_CATALOG` / `useFiasFonts().fonts`) to
 *      build a picker, and
 *   2. uses any `family` name in normal CSS / canvas text.
 *
 * For canvas / PDF rendering, call `ensureFontLoaded(family)` first and
 * `await` it — `<canvas>` (and Konva, Fabric, jsPDF html) measure glyph
 * metrics synchronously, so painting before the bytes land rasterizes the
 * fallback face. This is the SDK's only font responsibility; there is no
 * URL composition or `<link>` injection in plugin code (the host owns the
 * CDN and the `@font-face`).
 *
 * Distinct from `./themes/fonts` (`FONT_PAIRINGS`), which groups a few
 * families into curated heading/body theme pairings.
 */

import { useMemo } from 'react';
import { FONT_CATALOG, type FontCatalogEntry, type FontCategory } from './generated/fonts';

export { FONT_CATALOG };
export type { FontCatalogEntry, FontCategory };

/** OS-resident families resolve instantly — no `@font-face`, no fetch. */
const SYSTEM_FAMILIES = new Set(FONT_CATALOG.filter((f) => f.system).map((f) => f.family));

/**
 * How long to wait for a font's bytes before resolving anyway. Some
 * networks / ad-blockers silently stall the CDN fetch; without a bound,
 * `document.fonts.load` callers (e.g. an export that awaits every font)
 * would hang. On timeout the caller proceeds with whatever face is
 * currently resolvable — the same resolution shape as success.
 */
const FONT_READY_TIMEOUT_MS = 3000;

/**
 * Resolve once `family`'s bytes are ready to paint on a canvas. Never
 * rejects: resolves immediately for system fonts and non-browser
 * environments, and resolves on a {@link FONT_READY_TIMEOUT_MS} timeout if
 * the CDN is slow or blocked — so callers can always `await` without a
 * `try`/`catch`. Idempotent: re-calling for an already-loaded font
 * resolves instantly (the browser caches the face).
 */
export function ensureFontLoaded(family: string, sizePx = 16): Promise<void> {
  // SSR / unit tests with no DOM.
  if (typeof document === 'undefined') return Promise.resolve();
  // OS fonts are always available; the host injects no @font-face for them.
  if (SYSTEM_FAMILIES.has(family)) return Promise.resolve();
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fonts || typeof fonts.load !== 'function') return Promise.resolve();
  return raceWithTimeout(
    // The host-injected @font-face is already registered with
    // document.fonts, so this kicks the byte fetch and resolves when it
    // lands. Swallow rejections — a blocked fetch keeps the fallback face.
    fonts.load(`${sizePx}px "${family}"`).then(
      () => undefined,
      () => undefined,
    ),
    FONT_READY_TIMEOUT_MS,
  );
}

/** Resolve when `promise` settles OR `ms` elapses, whichever is first.
 *  Never rejects. */
function raceWithTimeout(promise: Promise<unknown>, ms: number): Promise<void> {
  return new Promise<void>((resolve) => {
    let settled = false;
    const done = (): void => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const timer = setTimeout(done, ms);
    void Promise.resolve(promise).finally(() => {
      clearTimeout(timer);
      done();
    });
  });
}

export interface FiasFontsApi {
  /** Every platform font available to the plugin (sorted by family). */
  fonts: readonly FontCatalogEntry[];
  /**
   * Resolve once `family`'s bytes are ready to paint (canvas / PDF safe).
   * Never rejects; resolves on a 3s timeout if the CDN is unreachable.
   */
  ensureFontLoaded: (family: string, sizePx?: number) => Promise<void>;
}

/**
 * Access the platform font catalog and the canvas-safe font loader.
 *
 * ```tsx
 * const { fonts, ensureFontLoaded } = useFiasFonts();
 * // build a picker from `fonts`, then before drawing to a canvas:
 * await ensureFontLoaded(selectedFamily);
 * ```
 */
export function useFiasFonts(): FiasFontsApi {
  return useMemo(() => ({ fonts: FONT_CATALOG, ensureFontLoaded }), []);
}
